import { Button, Modal, ModalFooter } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import { LoadingBlurDiv } from "@shared/components";

import { useAdminComplexQuery } from "../../api/complexes";

interface ComplexDetailsModalProps {
  open: boolean;
  complexId: string | null;
  onClose: () => void;
}

const Field = ({
  label,
  value,
  fallback,
}: {
  label: string;
  value: string | number | null | undefined;
  fallback: string;
}) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    <span className="text-sm break-words">
      {value === null || value === undefined || value === ""
        ? fallback
        : value}
    </span>
  </div>
);

export const ComplexDetailsModal = ({
  open,
  complexId,
  onClose,
}: ComplexDetailsModalProps) => {
  const { translate } = useLocalization();
  const { data, isFetching, isError } = useAdminComplexQuery(
    open ? complexId : null,
  );

  const fallback = translate("admin.complexes.details.notProvided");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={translate("admin.complexes.details.title")}
      content={
        <LoadingBlurDiv isLoading={isFetching}>
          <div className="px-[32px] py-[16px] flex flex-col gap-6 min-w-[480px]">
            {isError && (
              <p className="text-sm text-red-600">
                {translate("admin.complexes.details.loadError")}
              </p>
            )}

            <section className="flex flex-col gap-3">
              <h3 className="text-base font-semibold">
                {translate(
                  "admin.complexes.details.section.identification",
                )}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label={translate("admin.complexes.details.label.name")}
                  value={data?.name}
                  fallback={fallback}
                />
                <Field
                  label={translate(
                    "admin.complexes.details.label.fantasyName",
                  )}
                  value={data?.fantasyName}
                  fallback={fallback}
                />
                <Field
                  label={translate("admin.complexes.details.label.cnpj")}
                  value={data?.cnpj}
                  fallback={fallback}
                />
                <Field
                  label={translate(
                    "admin.complexes.details.label.municipalRegistration",
                  )}
                  value={data?.municipalRegistration}
                  fallback={fallback}
                />
                <Field
                  label={translate(
                    "admin.complexes.details.label.complexPhone",
                  )}
                  value={data?.phone}
                  fallback={fallback}
                />
              </div>
            </section>

            <section className="flex flex-col gap-3">
              <h3 className="text-base font-semibold">
                {translate("admin.complexes.details.section.contact")}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label={translate(
                    "admin.complexes.details.label.responsibleName",
                  )}
                  value={data?.responsibleName}
                  fallback={fallback}
                />
                <Field
                  label={translate(
                    "admin.complexes.details.label.responsiblePhone",
                  )}
                  value={data?.responsiblePhone}
                  fallback={fallback}
                />
              </div>
            </section>

            <section className="flex flex-col gap-3">
              <h3 className="text-base font-semibold">
                {translate("admin.complexes.details.section.address")}
              </h3>
              {data?.address ? (
                <div className="grid grid-cols-2 gap-4">
                  <Field
                    label={translate(
                      "admin.complexes.details.label.zipCode",
                    )}
                    value={data.address.zipCode}
                    fallback={fallback}
                  />
                  <Field
                    label={translate(
                      "admin.complexes.details.label.street",
                    )}
                    value={data.address.street}
                    fallback={fallback}
                  />
                  <Field
                    label={translate(
                      "admin.complexes.details.label.number",
                    )}
                    value={data.address.number}
                    fallback={fallback}
                  />
                  <Field
                    label={translate(
                      "admin.complexes.details.label.complement",
                    )}
                    value={data.address.complement}
                    fallback={fallback}
                  />
                  <Field
                    label={translate(
                      "admin.complexes.details.label.neighborhood",
                    )}
                    value={data.address.neighborhood}
                    fallback={fallback}
                  />
                  <Field
                    label={translate("admin.complexes.details.label.city")}
                    value={data.address.city}
                    fallback={fallback}
                  />
                  <Field
                    label={translate("admin.complexes.details.label.state")}
                    value={data.address.state}
                    fallback={fallback}
                  />
                  <Field
                    label={translate(
                      "admin.complexes.details.label.country",
                    )}
                    value={data.address.country}
                    fallback={fallback}
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {translate("admin.complexes.details.empty")}
                </p>
              )}
            </section>
          </div>

          <ModalFooter
            footer={
              <Button type="button" variant="primary" onClick={onClose}>
                {translate("admin.complexes.button.close")}
              </Button>
            }
          />
        </LoadingBlurDiv>
      }
    />
  );
};
